import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import GradientBackground from '@/components/ui/GradientBackground';
import TextReveal from '@/components/ui/TextReveal';
import { motion } from 'framer-motion';

const PrivacyPolicyPage = () => {
  return (
    <div className="bg-background text-foreground relative min-h-screen overflow-hidden">
      <GradientBackground />

      <div className="container mx-auto py-32 px-4 max-w-5xl relative z-10">
        {/* Header */}
        <div className="text-center mb-24">
          <div className="inline-block px-4 py-1.5 rounded-full border border-primary/30 bg-primary/10 backdrop-blur-md mb-6">
            <span className="text-primary text-sm font-semibold tracking-wider uppercase">Privacy & Data</span>
          </div>
          <TextReveal className="text-5xl md:text-8xl font-black tracking-tighter text-foreground leading-[0.9] mb-8">
            DATA INTEGRITY.
          </TextReveal>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed"
          >
            How we secure, process, and protect your digital footprint.
          </motion.p>
          <p className="text-sm text-muted-foreground mt-4 uppercase tracking-widest font-medium">Last Updated: December 15, 2025</p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.8 }}
        >
          <Card className="bg-foreground/5 border-foreground/10 backdrop-blur-xl overflow-hidden shadow-2xl">
            <CardContent className="p-0">
              <ScrollArea className="h-[800px] p-8 md:p-12">
                <div className="space-y-12 text-base md:text-lg leading-relaxed text-muted-foreground">

                  <section>
                    <h2 className="text-3xl font-bold text-foreground mb-6">1. Introduction</h2>
                    <p className="mb-4">
                      At <strong className="text-foreground">K&C Logistics</strong> ("we," "us," or "our"), we respect your privacy and are committed to protecting your personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our mobile application (the "Platform").
                    </p>
                    <p>
                      Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the Platform.
                    </p>
                  </section>

                  <Separator className="bg-foreground/10" />

                  <section>
                    <h2 className="text-3xl font-bold text-foreground mb-6">2. Information We Collect</h2>

                    <h3 className="text-xl font-bold text-foreground mb-3">2.1 Personal Data</h3>
                    <p className="mb-4">
                      We may collect personally identifiable information, such as your name, shipping address, email address, telephone number, and National ID number, when you register an account or post/accept a job. This information is necessary to provide our services and verify your identity.
                    </p>

                    <h3 className="text-xl font-bold text-foreground mb-3">2.2 Vehicle and Verification Data</h3>
                    <p className="mb-4">
                      For Transporters, Fleet Managers, and Fleet Drivers, we collect vehicle details (make, model, license plate, year), driver's license copies, vehicle registration documents, proof of ownership, company documentation (for Fleet Managers), and insurance certificates to verify eligibility and ensure safety standards. Fleet Managers must also provide business registration documents and tax identification numbers.
                    </p>

                    <h3 className="text-xl font-bold text-foreground mb-3">2.3 Location Data (Geo-Location)</h3>
                    <p className="mb-4">
                      <strong className="text-foreground">Mission Critical:</strong> We collect and process real-time location data from Transporters and Fleet Drivers while they are "Online" and during active Jobs ("In Transit"). For Fleet Drivers, location tracking is <strong className="text-foreground">mandatory and automatic</strong> - it starts when a trip begins and stops upon delivery completion. This enables Clients to track their cargo and allows us to provide accurate ETA updates and route optimization. Fleet Managers can monitor all vehicles in their fleet in real-time during active jobs.
                    </p>

                    <h3 className="text-xl font-bold text-foreground mb-3">2.4 Fleet Management Data</h3>
                    <p className="mb-4">
                      For Fleet Managers, we collect additional information including fleet size, driver assignments, job dispatch records, payment distribution records to drivers, and fleet performance metrics. This data is necessary to facilitate fleet operations and ensure transparency in the platform.
                    </p>

                    <h3 className="text-xl font-bold text-foreground mb-3">2.5 Financial Data</h3>
                    <p className="mb-4">
                      We do not store your full credit card or bank account details on our servers. Financial transactions are processed by our third-party payment processor, <strong className="text-foreground">PayChangu</strong>. We store only the transaction history, transaction IDs, and tokenized references necessary for payment reconciliation and customer support. For Fleet Managers, we also maintain records of payments made to Fleet Drivers for transparency and dispute resolution.
                    </p>

                    <h3 className="text-xl font-bold text-foreground mb-3">2.6 Usage Data</h3>
                    <p className="mb-4">
                      We automatically collect certain information when you access the Platform, including your IP address, browser type, operating system, access times, pages viewed, and the page you visited before navigating to our Platform. This data helps us improve our services and user experience.
                    </p>

                    <h3 className="text-xl font-bold text-foreground mb-3">2.7 Device Information</h3>
                    <p>
                      We may collect information about your mobile device, including device ID, model, manufacturer, operating system version, and mobile network information. This helps us optimize the Platform for your specific device and troubleshoot technical issues.
                    </p>
                  </section>

                  <Separator className="bg-foreground/10" />

                  <section>
                    <h2 className="text-3xl font-bold text-foreground mb-6">3. How We Use Your Information</h2>
                    <p className="mb-4">We use the information we collect to:</p>
                    <ul className="list-disc pl-5 space-y-3 marker:text-primary">
                      <li>Create and manage your account and provide you with customer support.</li>
                      <li>Facilitate the logistics and transportation services, including matching Clients with Transporters, Fleet Managers, and Fleet Drivers.</li>
                      <li>Enable Fleet Managers to manage their fleet operations, assign jobs to drivers, and track fleet performance.</li>
                      <li>Process payments, refunds, and financial transactions securely, including facilitating payments between Fleet Managers and their drivers.</li>
                      <li>Verify the identity of Transporters, Fleet Managers, and Fleet Drivers, and the legitimacy of vehicles to maintain platform safety.</li>
                      <li>Send you notifications regarding your account, Jobs, job assignments (for Fleet Drivers), and platform updates (e.g., "Job Accepted", "Driver Assigned", "Driver Arrived", "Delivery Complete").</li>
                      <li>Monitor and analyze usage patterns and trends to improve your experience with the Platform.</li>
                      <li>Prevent fraudulent transactions, monitor against theft, and protect against criminal activity.</li>
                      <li>Comply with legal obligations and respond to law enforcement requests.</li>
                      <li>Conduct research and development to improve our services and develop new features.</li>
                      <li>Send you marketing communications (with your consent where required by law).</li>
                    </ul>
                  </section>

                  <Separator className="bg-foreground/10" />

                  <section>
                    <h2 className="text-3xl font-bold text-foreground mb-6">4. Disclosure of Your Information</h2>
                    <p className="mb-4">
                      We may share information we have collected about you in certain situations. Your information may be disclosed as follows:
                    </p>

                    <h3 className="text-xl font-bold text-foreground mb-3">4.1 By Law or to Protect Rights</h3>
                    <p className="mb-4">
                      If we believe the release of information about you is necessary to respond to legal process, to investigate or remedy potential violations of our policies, or to protect the rights, property, and safety of others, we may share your information as permitted or required by any applicable law, rule, or regulation.
                    </p>

                    <h3 className="text-xl font-bold text-foreground mb-3">4.2 Third-Party Service Providers</h3>
                    <p className="mb-4">
                      We may share your information with third parties that perform services for us or on our behalf, including payment processing (PayChangu), data analysis, email delivery, hosting services, customer service, and marketing assistance. These service providers are contractually obligated to protect your information and use it only for the purposes we specify.
                    </p>

                    <h3 className="text-xl font-bold text-foreground mb-3">4.3 Other Users</h3>
                    <p className="mb-4">
                      When you interact with other users on the Platform (e.g., a Client booking a Transporter or Fleet Manager), they may see your name, profile photo, ratings, and descriptions of your activity. Transporters and Fleet Drivers will see the pickup and delivery addresses provided by Clients. Fleet Managers can see the profiles and performance data of their Fleet Drivers. Fleet Drivers can see job details assigned to them by their Fleet Manager. This information sharing is necessary to facilitate the services.
                    </p>

                    <h3 className="text-xl font-bold text-foreground mb-3">4.4 Business Transfers</h3>
                    <p>
                      We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company.
                    </p>
                  </section>

                  <Separator className="bg-foreground/10" />

                  <section>
                    <h2 className="text-3xl font-bold text-foreground mb-6">5. Data Security</h2>
                    <p className="mb-4">
                      We use administrative, technical, and physical security measures to help protect your personal information. These measures include encryption of data in transit and at rest, secure server infrastructure, regular security audits, and access controls limiting who can view your personal information.
                    </p>
                    <p>
                      While we have taken reasonable steps to secure the personal information you provide to us, please be aware that despite our efforts, no security measures are perfect or impenetrable, and no method of data transmission can be guaranteed against any interception or other type of misuse. Any information disclosed online is vulnerable to interception and misuse by unauthorized parties.
                    </p>
                  </section>

                  <Separator className="bg-foreground/10" />

                  <section>
                    <h2 className="text-3xl font-bold text-foreground mb-6">6. Data Retention</h2>
                    <p className="mb-4">
                      We will retain your personal information only for as long as is necessary for the purposes set out in this Privacy Policy. We will retain and use your information to the extent necessary to comply with our legal obligations (for example, if we are required to retain your data to comply with applicable laws), resolve disputes, enforce our legal agreements and policies, and maintain business records.
                    </p>
                    <p>
                      When we no longer need your personal information, we will securely delete or anonymize it. If this is not possible (for example, because your personal information has been stored in backup archives), then we will securely store your personal information and isolate it from any further processing until deletion is possible.
                    </p>
                  </section>

                  <Separator className="bg-foreground/10" />

                  <section>
                    <h2 className="text-3xl font-bold text-foreground mb-6">7. Your Data Protection Rights</h2>
                    <p className="mb-4">
                      Depending on your location, you may have the following rights regarding your personal data:
                    </p>
                    <ul className="list-disc pl-5 space-y-2 marker:text-primary">
                      <li><strong className="text-foreground">The right to access</strong> – You have the right to request copies of your personal data. We may charge you a small fee for this service.</li>
                      <li><strong className="text-foreground">The right to rectification</strong> – You have the right to request that we correct any information you believe is inaccurate or complete information you believe is incomplete.</li>
                      <li><strong className="text-foreground">The right to erasure</strong> – You have the right to request that we erase your personal data, under certain conditions.</li>
                      <li><strong className="text-foreground">The right to restrict processing</strong> – You have the right to request that we restrict the processing of your personal data, under certain conditions.</li>
                      <li><strong className="text-foreground">The right to object to processing</strong> – You have the right to object to our processing of your personal data, under certain conditions.</li>
                      <li><strong className="text-foreground">The right to data portability</strong> – You have the right to request that we transfer the data that we have collected to another organization, or directly to you, under certain conditions.</li>
                    </ul>
                    <p className="mt-4">
                      To exercise any of these rights, please contact us using the contact information provided below. We will respond to your request within 30 days.
                    </p>
                  </section>

                  <Separator className="bg-foreground/10" />

                  <section>
                    <h2 className="text-3xl font-bold text-foreground mb-6">8. Cookies and Tracking Technologies</h2>
                    <p className="mb-4">
                      We use cookies and similar tracking technologies to track activity on our Platform and hold certain information. Cookies are files with a small amount of data which may include an anonymous unique identifier. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our Platform.
                    </p>
                    <p>
                      We use both session cookies (which expire when you close your browser) and persistent cookies (which stay on your device until you delete them) for various purposes including authentication, preferences, analytics, and advertising.
                    </p>
                  </section>

                  <Separator className="bg-foreground/10" />

                  <section>
                    <h2 className="text-3xl font-bold text-foreground mb-6">9. Third-Party Websites</h2>
                    <p>
                      Our Platform may contain links to third-party websites that are not operated by us. If you click on a third-party link, you will be directed to that third party's site. We strongly advise you to review the Privacy Policy of every site you visit. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party sites or services.
                    </p>
                  </section>

                  <Separator className="bg-foreground/10" />

                  <section>
                    <h2 className="text-3xl font-bold text-foreground mb-6">10. Children's Privacy</h2>
                    <p className="mb-4">
                      Our Platform is not intended for use by children under the age of 18. We do not knowingly collect personally identifiable information from children under 18. If you are a parent or guardian and you are aware that your child has provided us with personal data, please contact us.
                    </p>
                    <p>
                      If we become aware that we have collected personal data from children without verification of parental consent, we take steps to remove that information from our servers.
                    </p>
                  </section>

                  <Separator className="bg-foreground/10" />

                  <section>
                    <h2 className="text-3xl font-bold text-foreground mb-6">11. International Data Transfers</h2>
                    <p className="mb-4">
                      Your information, including personal data, may be transferred to — and maintained on — computers located outside of your state, province, country, or other governmental jurisdiction where the data protection laws may differ from those of your jurisdiction.
                    </p>
                    <p>
                      If you are located outside Malawi and choose to provide information to us, please note that we transfer the data, including personal data, to Malawi and process it there. Your consent to this Privacy Policy followed by your submission of such information represents your agreement to that transfer.
                    </p>
                  </section>

                  <Separator className="bg-foreground/10" />

                  <section>
                    <h2 className="text-3xl font-bold text-foreground mb-6">12. Changes to This Privacy Policy</h2>
                    <p className="mb-4">
                      We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date at the top of this Privacy Policy.
                    </p>
                    <p>
                      You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page. Your continued use of the Platform after we post any modifications to the Privacy Policy will constitute your acknowledgment of the modifications and your consent to abide and be bound by the modified Privacy Policy.
                    </p>
                  </section>

                  <Separator className="bg-foreground/10" />

                  <section>
                    <h2 className="text-3xl font-bold text-foreground mb-6">13. Contact Us</h2>
                    <p className="mb-4">
                      If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at:
                    </p>
                    <div className="p-6 bg-background/30 rounded-2xl border border-foreground/5 backdrop-blur-md">
                      <p className="text-foreground font-bold mb-2">K&C Logistics Privacy Officer</p>
                      <ul className="space-y-1 text-sm">
                        <li>Email: privacy@kclogistics.mw</li>
                        <li>Phone: +265 999 123 456</li>
                        <li>Address: City Centre, Lilongwe, Malawi</li>
                      </ul>
                    </div>
                  </section>

                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;